// Persistent per-table column sort state.  Remembers each user's last-used
// sort column + direction across navigations, page refreshes, and days.
//
// Usage:
//   const sort = useTableSort('matters', { key: 'title', dir: 'asc' })
//   const rows = sort.apply(items, {
//     title: (row) => row.title,
//     created_at: (row) => new Date(row.created_at),
//     wip: (row) => parseFloat(row.wip_amount || 0),
//   })
//   ...
//   <th onClick={() => sort.toggle('title')}>
//     Title <SortIcon dir={sort.key === 'title' ? sort.dir : null} />
//   </th>
//
// Storage key: `lextrack_sort_${tableId}` = { key, dir }.  Missing key means
// use the default provided to the hook.

import { useState, useEffect, useCallback } from 'react'

const STORAGE_PREFIX = 'lextrack_sort_'

function readSaved(tableId) {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + tableId)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (parsed && typeof parsed.key === 'string' && (parsed.dir === 'asc' || parsed.dir === 'desc')) {
      return parsed
    }
  } catch { /* corrupt localStorage — fall through */ }
  return null
}

function writeSaved(tableId, state) {
  try {
    localStorage.setItem(STORAGE_PREFIX + tableId, JSON.stringify(state))
  } catch { /* quota / private-mode — ignore */ }
}

export function useTableSort(tableId, defaultState = { key: null, dir: 'asc' }) {
  const [state, setState] = useState(() => readSaved(tableId) || defaultState)

  // Persist whenever it changes
  useEffect(() => {
    writeSaved(tableId, state)
  }, [tableId, state])

  // Toggle: clicking a column that's already active flips the direction;
  // clicking a different column switches to that column with default 'asc'.
  const toggle = useCallback((key) => {
    setState(prev => {
      if (prev.key === key) return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      return { key, dir: 'asc' }
    })
  }, [])

  const reset = useCallback(() => setState(defaultState), [defaultState.key, defaultState.dir])

  // Apply the sort to an array using an accessor map.  If key isn't in the
  // accessors OR is null, returns items unchanged (no sort).
  const apply = useCallback((items, accessors) => {
    if (!Array.isArray(items)) return items
    if (!state.key || !accessors || !accessors[state.key]) return items
    const acc = accessors[state.key]
    const sign = state.dir === 'desc' ? -1 : 1
    // Make a shallow copy to avoid mutating input.
    return [...items].sort((a, b) => {
      const va = acc(a)
      const vb = acc(b)
      if (va == null && vb == null) return 0
      if (va == null) return 1
      if (vb == null) return -1
      if (typeof va === 'string' && typeof vb === 'string') return sign * va.localeCompare(vb)
      if (va < vb) return -1 * sign
      if (va > vb) return 1 * sign
      return 0
    })
  }, [state])

  return {
    key: state.key,
    dir: state.dir,
    toggle,
    reset,
    apply,
  }
}

// Small helper — renders an ▲/▼ icon based on the current sort direction.
// Neutral icon (◇) when this column is not the active sort.
export function SortIcon({ dir }) {
  if (dir === 'asc') return <span aria-hidden="true" className="text-primary-600 ml-1">▲</span>
  if (dir === 'desc') return <span aria-hidden="true" className="text-primary-600 ml-1">▼</span>
  return <span aria-hidden="true" className="text-gray-300 ml-1">⇅</span>
}

export default useTableSort
